import { Urls } from 'js/src/client';
import {
    ALL_VIDEOS_VIEW_ID,
    CREATE_VIEW_ID,
    TRASH_VIDEOS_VIEW_ID,
} from 'js/src/data/dashboard/dashboard-types';
import { COOKIE_KEYS, roleCanCreateVideos } from 'js/src/libs/consts';
import Cookies from 'js-cookie';

/**
 * Why no router (react-router / TanStack / etc.)?
 *
 * The dashboard is the only part of the app that needs sub-tab URLs — everything
 * else is server-rendered Django pages reached by full navigation. Pulling in a
 * client-side router for one screen would mean wrapping the whole entry point in
 * a <Router>, teaching every existing link/redirect about it, and shipping the
 * library to every page that doesn't use it.
 *
 * Instead we keep the dashboard's routing concerns local: a tiny path↔viewId
 * mapping here, a `popstate` listener in `DashboardRouteSync`, and
 * `pushState` for in-app navigation via `navigateToDashboardPath`. The view
 * itself already lives in Redux (`activeView`), so the URL is just another
 * input that feeds the same state — no router needed to own it.
 *
 * If/when more areas of the app become SPA-routed, revisit this.
 */

export const DASHBOARD_ROUTES = {
    CREATE: `${Urls.DASHBOARD}create/`,
    VIDEOS: `${Urls.DASHBOARD}videos/`,
    TRASH: `${Urls.DASHBOARD}trash/`,
} as const;

export function userCanAccessCreateView(): boolean {
    return typeof SERVER !== 'undefined' && !!SERVER.USER && roleCanCreateVideos(SERVER.USER.role);
}

export function getDefaultDashboardPath(): string {
    if (userCanAccessCreateView()) {
        return DASHBOARD_ROUTES.CREATE;
    }
    return DASHBOARD_ROUTES.VIDEOS;
}

export function pathForView(viewId: number): string {
    if (viewId === CREATE_VIEW_ID) return DASHBOARD_ROUTES.CREATE;
    if (viewId === ALL_VIDEOS_VIEW_ID) return DASHBOARD_ROUTES.VIDEOS;
    if (viewId === TRASH_VIDEOS_VIEW_ID) return DASHBOARD_ROUTES.TRASH;
    return `${Urls.DASHBOARD}projects/${viewId}/`;
}

/**
 * Resolve the active view ID from a URL pathname. Returns null when the path
 * doesn't match a known dashboard view (e.g. project route with non-numeric id).
 * Trailing slashes are normalized so `/dashboard/create` and `/dashboard/create/`
 * both resolve to the same view.
 */
export function viewIdFromPath(pathname: string | undefined | null): number | null {
    if (typeof pathname !== 'string') return null;
    const withSlash = pathname.endsWith('/') ? pathname : `${pathname}/`;
    if (withSlash === DASHBOARD_ROUTES.CREATE) return CREATE_VIEW_ID;
    if (withSlash === DASHBOARD_ROUTES.VIDEOS) return ALL_VIDEOS_VIEW_ID;
    if (withSlash === DASHBOARD_ROUTES.TRASH) return TRASH_VIDEOS_VIEW_ID;
    const projectMatch = withSlash.match(/^\/dashboard\/projects\/(\d+)\/$/);
    if (projectMatch) {
        return parseInt(projectMatch[1], 10);
    }
    return null;
}

/**
 * Programmatically navigate to a dashboard URL without a full page reload, and
 * notify route subscribers so the in-app router state stays in sync.
 *
 * Use this instead of `window.history.pushState` directly — `pushState` does
 * not emit any event, so listeners would otherwise miss the navigation.
 */
export function navigateToDashboardPath(path: string) {
    if (window.location.pathname === path) return;
    window.history.pushState(null, '', path);
    window.dispatchEvent(new PopStateEvent('popstate'));
}

/**
 * The last dashboard sub-path the user visited, scoped by workspace so a path
 * for one workspace doesn't follow the user into another. Used to bounce bare
 * `/dashboard/` visits back to where the user left off.
 */
export function getLastDashboardPath(): string | null {
    try {
        const raw = Cookies.get(COOKIE_KEYS.LAST_DASHBOARD_PATH);
        if (!raw) return null;
        const { workspaceId, path } = JSON.parse(raw);
        if (workspaceId !== SERVER.USER.workspace_id) return null;
        return typeof path === 'string' ? path : null;
    } catch {
        return null;
    }
}

export function setLastDashboardPath(path: string) {
    Cookies.set(
        COOKIE_KEYS.LAST_DASHBOARD_PATH,
        JSON.stringify({ workspaceId: SERVER.USER.workspace_id, path }),
        { expires: 365 },
    );
}
